import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, Lock } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EomReconciliationMonthModal } from "./EomReconciliationMonthModal";
import {
  buildEomMonthRange,
  fetchEomOverview,
  fetchEomSavedMonths,
  firstOfUtcMonthIso,
  formatEomMoney,
  formatMonthLabel,
  type EomMonthListRow,
  type EomOverviewRow,
} from "./eomReconciliationApi";

const HELP_TEXT = [
  "End-of-month inventory reconciliation between Megaventory target balances and QuickBooks Online account balances.",
  "",
  "Open a month from the table to enter Target values (Megaventory) and QBO values, then review the computed journal entry.",
  "Locked months are view-only. Submit the journal entry in QuickBooks Online, then record the JE name and link to finish the month.",
  "The hub does not post to QuickBooks — copy the journal entry into QBO manually.",
].join(" ");

function AccountsOverviewHoverHint({ rows }: { rows: EomOverviewRow[] }) {
  return (
    <HoverHintTrigger
      align="start"
      trigger={
        <button
          type="button"
          className="text-sm font-medium text-blue-700 underline decoration-blue-700/30 underline-offset-2 hover:text-blue-800"
        >
          accounts overview
        </button>
      }
    >
      <div className="max-w-[42rem] space-y-2 p-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Account mapping</p>
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-left text-[0.65rem] uppercase tracking-wide text-slate-500">
              <th className="px-2 py-1.5">QBO account</th>
              <th className="px-2 py-1.5">MV equivalent</th>
              <th className="px-2 py-1.5">Positive is</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.qbo_account} className="border-b border-slate-100">
                <td className="px-2 py-1.5 font-medium text-slate-900">{row.qbo_account}</td>
                <td className="px-2 py-1.5 text-slate-700">{row.mv_equivalent}</td>
                <td className="px-2 py-1.5 text-slate-700">{row.positive_is}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </HoverHintTrigger>
  );
}

type EomMonthTableRow = {
  month: string;
  label: string;
  savedRow: EomMonthListRow | undefined;
};

type EomMonthSortKey = "month" | "magnitude_of_je" | "target_cogs" | "je_name";

function JeTableCell({ row }: { row: EomMonthListRow | undefined }) {
  if (!row?.qbo_je_name) {
    return <span className="text-slate-400">—</span>;
  }
  if (!row.qbo_je_url) {
    return <span className="text-slate-800">{row.qbo_je_name}</span>;
  }
  return (
    <a
      href={row.qbo_je_url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 font-medium text-blue-700 hover:text-blue-800"
      onClick={(event) => event.stopPropagation()}
    >
      <span>{row.qbo_je_name}</span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="sr-only">Open journal entry in QuickBooks Online</span>
    </a>
  );
}

export function EomReconciliationPage() {
  const canWrite = useHubModuleWrite("accounting");
  const [openMonth, setOpenMonth] = useState<string | null>(null);

  const overviewQuery = useQuery({
    queryKey: ["accounting", "eom-reconciliation", "overview"],
    queryFn: fetchEomOverview,
  });

  const savedMonthsQuery = useQuery({
    queryKey: ["accounting", "eom-reconciliation", "months"],
    queryFn: fetchEomSavedMonths,
  });

  const savedByMonth = useMemo(() => {
    const map = new Map<string, EomMonthListRow>();
    for (const row of savedMonthsQuery.data?.rows ?? []) {
      map.set(row.reconciliation_month, row);
    }
    return map;
  }, [savedMonthsQuery.data?.rows]);

  const monthRows = useMemo(() => {
    const savedMonths = savedMonthsQuery.data?.months ?? [];
    const latestSaved = savedMonths[0];
    const currentMonth = firstOfUtcMonthIso();
    const worksheetEnd = `${new Date().getUTCFullYear()}-12-01`;
    const endMonth = [latestSaved, currentMonth, worksheetEnd].filter(Boolean).sort().at(-1) ?? worksheetEnd;
    const range = buildEomMonthRange("2025-12-01", endMonth);
    return range.map((month) => {
      const savedRow = savedByMonth.get(month);
      return {
        month,
        label: formatMonthLabel(month),
        savedRow,
      };
    });
  }, [savedByMonth, savedMonthsQuery.data?.months]);

  const monthColumns = useMemo((): HubDataTableColumn<EomMonthTableRow, EomMonthSortKey>[] => {
    return [
      {
        id: "month",
        header: "Month",
        sortKey: "month",
        sortValue: (row) => row.month,
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5 font-medium text-blue-700">
            {row.savedRow?.locked ? <Lock className="h-3.5 w-3.5 text-slate-500" aria-hidden /> : null}
            {row.label}
          </span>
        ),
      },
      {
        id: "magnitude_of_je",
        header: "Magnitude of JE",
        sortKey: "magnitude_of_je",
        align: "right",
        overflow: "nowrap",
        sortValue: (row) => row.savedRow?.magnitude_of_je ?? "",
        cellClassName: "font-mono text-slate-800",
        cell: (row) => formatEomMoney(row.savedRow?.magnitude_of_je),
      },
      {
        id: "target_cogs",
        header: "Target COGS",
        sortKey: "target_cogs",
        align: "right",
        overflow: "nowrap",
        sortValue: (row) => row.savedRow?.target_cogs ?? "",
        cellClassName: "font-mono text-slate-800",
        cell: (row) => formatEomMoney(row.savedRow?.target_cogs),
      },
      {
        id: "je_name",
        header: "JE",
        sortKey: "je_name",
        sortValue: (row) => row.savedRow?.qbo_je_name ?? "",
        cell: (row) => <JeTableCell row={row.savedRow} />,
      },
    ];
  }, []);

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint={HELP_TEXT}>EOM reconciliation</PageTitleWithHelp>

      <HubDataTable
        title="Months"
        headerExtra={
          <div className="flex flex-col gap-1 text-sm text-slate-600 sm:items-end">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>See the</span>
              {overviewQuery.isLoading ? (
                <span className="inline-flex items-center gap-1 text-slate-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  loading…
                </span>
              ) : (
                <AccountsOverviewHoverHint rows={overviewQuery.data?.rows ?? []} />
              )}
              <span>for QBO account mapping.</span>
            </div>
            {!canWrite ? (
              <p className="text-xs text-slate-500">Read-only — you can open months but cannot save changes.</p>
            ) : null}
          </div>
        }
        columns={monthColumns}
        rows={monthRows}
        rowKey={(row) => row.month}
        onRowClick={(row) => setOpenMonth(row.month)}
        loading={savedMonthsQuery.isLoading}
        loadingMessage="Loading months…"
        empty="No reconciliation months yet."
        fillViewport={false}
      />

      {openMonth ? (
        <EomReconciliationMonthModal
          month={openMonth}
          canWrite={canWrite}
          onClose={() => setOpenMonth(null)}
        />
      ) : null}
    </div>
  );
}
